import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { hostApiClient } from '../../../hostApiClient';
import { useThemeContext } from '../../../contexts/ThemeContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import {
  DEFAULT_MANUAL_BOOKING_COUNTRY_CODE,
  DEFAULT_MANUAL_BOOKING_LOCAL_LENGTH,
  composeManualBookingPhoneNumber,
} from '../../../utils/manualHostBooking';

export interface AddStudentDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

const fieldSx = (mode: string) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
    },
  },
});

export function AddStudentDialog({ open, onClose, onAdded }: AddStudentDialogProps) {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const sx = fieldSx(mode);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [sendSms, setSendSms] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirstName('');
    setLastName('');
    setPhoneLocal('');
    setPhoneError(false);
    setSendSms(true);
    setSubmitError(null);
  }, [open]);

  const handleSubmit = async () => {
    const phoneNumber = composeManualBookingPhoneNumber(
      DEFAULT_MANUAL_BOOKING_COUNTRY_CODE,
      phoneLocal
    );
    if (!phoneNumber || phoneLocal.length !== DEFAULT_MANUAL_BOOKING_LOCAL_LENGTH) {
      setPhoneError(true);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const trimmedFirst = firstName.trim() || null;
      const trimmedLast = lastName.trim() || null;
      const created = await hostApiClient.createHostStudent({
        phoneNumber,
        sendSms,
        firstName: trimmedFirst,
        lastName: trimmedLast,
      });
      if (created.id && (trimmedFirst || trimmedLast)) {
        await hostApiClient.updateHostStudentProfile(created.id, {
          firstName: trimmedFirst,
          lastName: trimmedLast,
          dateOfBirth: null,
          guardianContact: null,
          notes: null,
        });
      }
      onAdded();
      onClose();
    } catch (error) {
      setSubmitError(
        extractErrorMessage(error) ||
          t('hostStudents.add.error', 'Could not add student. Try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.35rem',
          letterSpacing: '-0.02em',
          pt: 2.5,
          px: 3,
        }}
      >
        {t('hostStudents.add.title', 'Add student')}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {t(
            'hostStudents.add.explainer',
            'Enter the student’s school profile name and phone number. If they don’t have an account yet, one will be created. An SMS invite can be sent the same way as a calendar booking for a new client.'
          )}
        </Typography>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}
        <Stack spacing={2}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: '0.08em',
              fontWeight: 700,
              color: 'text.secondary',
              lineHeight: 1,
              mb: -0.5,
            }}
          >
            {t('hostStudents.add.profileSection', 'School profile')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              autoFocus
              label={t('hostStudents.profile.firstName', 'First name')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              disabled={submitting}
              sx={sx}
            />
            <TextField
              label={t('hostStudents.profile.lastName', 'Last name')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              disabled={submitting}
              sx={sx}
            />
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' },
              alignItems: 'flex-start',
              gap: 1.5,
              width: '100%',
            }}
          >
            <TextField
              label={t('hostStudents.add.phoneLabel', 'Phone number')}
              value={phoneLocal}
              onChange={(e) => {
                const value = e.target.value;
                if (new RegExp(`^\\d{0,${DEFAULT_MANUAL_BOOKING_LOCAL_LENGTH}}$`).test(value)) {
                  setPhoneLocal(value);
                  if (phoneError) setPhoneError(false);
                }
              }}
              fullWidth
              error={phoneError}
              helperText={
                phoneError
                  ? t(
                      'hostStudents.add.phoneRequired',
                      'Enter a {{count}}-digit phone number.',
                      { count: DEFAULT_MANUAL_BOOKING_LOCAL_LENGTH }
                    )
                  : undefined
              }
              disabled={submitting}
              sx={sx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{DEFAULT_MANUAL_BOOKING_COUNTRY_CODE}</InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              sx={{
                m: 0,
                mt: { xs: 0, sm: 1 },
                width: '100%',
                alignItems: 'center',
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  lineHeight: 1.3,
                },
              }}
              control={
                <Checkbox
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={t('hostStudents.add.sendSms', 'Send invite by SMS')}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={22} /> : t('hostStudents.add.confirm', 'Add student')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
